import { Helmet } from 'react-helmet-async';

// Default SEO configuration for Keystone Properties
const defaultSEO = {
  siteName: 'Keystone Properties',
  siteUrl: 'https://thekeystoneproperties.co.uk',
  defaultTitle: 'Keystone Properties | Property Management & Lettings in West Yorkshire',
  defaultDescription: 'Professional property management, lettings and investment services in Leeds, Bradford, Batley, Dewsbury and West Yorkshire. Expert landlord support and tenant services.',
  defaultImage: 'https://thekeystoneproperties.co.uk/og-image.jpg',
  twitterHandle: '@keystoneprops',
  phone: '01924 520163',
  address: {
    streetAddress: '17 Commercial Street',
    addressLocality: 'Batley',
    addressRegion: 'West Yorkshire',
    postalCode: 'WF17 5HJ',
    addressCountry: 'GB'
  }
};

// Organization Schema for the entire site
const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'RealEstateAgent',
  name: 'Keystone Properties',
  description: defaultSEO.defaultDescription,
  url: defaultSEO.siteUrl,
  logo: 'https://thekeystoneproperties.co.uk/logo.png',
  telephone: defaultSEO.phone,
  email: 'info@thekeystoneproperties.co.uk',
  address: {
    '@type': 'PostalAddress',
    ...defaultSEO.address
  },
  areaServed: [
    { '@type': 'City', name: 'Leeds' },
    { '@type': 'City', name: 'Bradford' },
    { '@type': 'City', name: 'Batley' },
    { '@type': 'City', name: 'Dewsbury' },
    { '@type': 'AdministrativeArea', name: 'West Yorkshire' }
  ],
  sameAs: [
    'https://www.facebook.com/keystoneproperties',
    'https://www.instagram.com/keystoneproperties',
    'https://www.linkedin.com/company/keystone-properties-uk'
  ],
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '17:30'
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Saturday',
      opens: '10:00',
      closes: '14:00'
    }
  ],
  priceRange: '££'
};

// Local Business Schema
const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${defaultSEO.siteUrl}/#localbusiness`,
  name: 'Keystone Properties',
  image: defaultSEO.defaultImage,
  telephone: defaultSEO.phone,
  email: 'info@thekeystoneproperties.co.uk',
  address: {
    '@type': 'PostalAddress',
    ...defaultSEO.address
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 53.7129,
    longitude: -1.6357
  },
  url: defaultSEO.siteUrl,
  priceRange: '££',
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    reviewCount: '47'
  }
};

/**
 * SEO Component for page-level meta tags and structured data
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.canonical - Canonical URL path (e.g., '/properties')
 * @param {string} props.image - OG image URL
 * @param {string} props.type - OG type (website, article, etc.)
 * @param {Object} props.schema - Additional schema.org structured data
 * @param {boolean} props.noindex - Whether to noindex the page
 * @param {Array} props.keywords - Page-specific keywords
 */
const SEO = ({
  title,
  description,
  canonical,
  image,
  type = 'website',
  schema,
  noindex = false,
  keywords = []
}) => {
  const pageTitle = title 
    ? `${title} | ${defaultSEO.siteName}`
    : defaultSEO.defaultTitle;
  
  const pageDescription = description || defaultSEO.defaultDescription;
  const pageImage = image || defaultSEO.defaultImage;
  const canonicalUrl = canonical 
    ? `${defaultSEO.siteUrl}${canonical}`
    : defaultSEO.siteUrl;

  // Default keywords for all pages
  const defaultKeywords = [
    'property management',
    'lettings',
    'West Yorkshire',
    'Leeds',
    'Bradford',
    'Batley',
    'Dewsbury',
    'landlord services',
    'rental properties',
    'property investment'
  ];

  const allKeywords = [...defaultKeywords, ...keywords].join(', ');

  // Combine schemas
  const schemas = [organizationSchema, localBusinessSchema];
  if (schema) {
    schemas.push(schema);
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={allKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:site_name" content={defaultSEO.siteName} />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      <meta name="twitter:site" content={defaultSEO.twitterHandle} />

      {/* Geo Tags for Local SEO */}
      <meta name="geo.region" content="GB-WYK" />
      <meta name="geo.placename" content="Batley, West Yorkshire" />
      <meta name="geo.position" content="53.7129;-1.6357" />
      <meta name="ICBM" content="53.7129, -1.6357" />

      {/* Additional SEO Meta */}
      <meta name="author" content="Keystone Properties" />
      <meta name="publisher" content="Keystone Properties" />
      <meta name="copyright" content="Keystone Properties" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />

      {/* Mobile Optimization */}
      <meta name="format-detection" content="telephone=yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Keystone Properties" />

      {/* Structured Data */}
      {schemas.map((s, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
};

// Pre-configured SEO components for each page
export const HomeSEO = () => (
  <SEO
    canonical="/"
    keywords={['property management company', 'best letting agent West Yorkshire', 'landlord services Batley']}
    schema={{
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Home',
      description: defaultSEO.defaultDescription,
      url: defaultSEO.siteUrl,
      mainEntity: {
        '@type': 'RealEstateAgent',
        name: 'Keystone Properties'
      }
    }}
  />
);

export const PropertiesSEO = () => (
  <SEO
    title="Properties to Rent"
    description="Browse available rental properties in Leeds, Bradford, Batley, Dewsbury and West Yorkshire. Find houses, apartments and flats to rent with Keystone Properties."
    canonical="/properties"
    keywords={['properties to rent', 'houses to rent Leeds', 'flats to rent Bradford', 'rental properties Batley']}
    schema={{
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Available Properties',
      description: 'Rental properties available in West Yorkshire',
      itemListElement: []
    }}
  />
);

export const PackagesSEO = () => (
  <SEO
    title="Property Management Packages & Pricing"
    description="Transparent property management pricing for landlords across Leeds, Batley, Dewsbury, Bradford and West Yorkshire. Tenant Find, Rent Collection, Fully Managed and Investor & HMO packages — clear fees, no nasty surprises."
    canonical="/services/packages"
    keywords={[
      'property management Leeds',
      'property management Batley',
      'property management Dewsbury',
      'property management Bradford',
      'property management West Yorkshire',
      'property management Yorkshire',
      'letting agents Leeds',
      'landlord services Leeds',
      'HMO management Leeds',
      'fully managed letting service West Yorkshire',
      'tenant find only Leeds',
      'rent collection Batley',
    ]}
  />
);

export const PropertyDetailSEO = ({ property }) => (
  <SEO
    title={property?.title || 'Property Details'}
    description={property?.description?.substring(0, 160) || 'View details of this rental property in West Yorkshire.'}
    canonical={`/properties/${property?.id}`}
    image={property?.images?.[0]}
    keywords={[
      `${property?.bedrooms} bed ${property?.property_type?.toLowerCase()}`,
      `rent ${property?.location}`,
      `property ${property?.location}`
    ]}
    schema={{
      '@context': 'https://schema.org',
      '@type': 'RealEstateListing',
      name: property?.title,
      description: property?.description,
      url: `${defaultSEO.siteUrl}/properties/${property?.id}`,
      image: property?.images,
      address: {
        '@type': 'PostalAddress',
        streetAddress: property?.address,
        addressLocality: property?.location,
        addressRegion: 'West Yorkshire',
        addressCountry: 'GB'
      },
      offers: {
        '@type': 'Offer',
        price: property?.price,
        priceCurrency: 'GBP',
        availability: property?.available ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'
      }
    }}
  />
);

export const ServicesSEO = ({ service }) => {
  const serviceConfig = {
    'property-management': {
      title: 'Property Management Services',
      description: 'Full property management services in West Yorkshire. Tenant find, rent collection, maintenance coordination, and regular inspections for landlords.',
      keywords: ['property management', 'full management service', 'landlord services']
    },
    'lettings': {
      title: 'Lettings Services',
      description: 'Professional lettings services in Leeds, Bradford, Batley and Dewsbury. Tenant referencing, marketing, and tenancy setup for landlords.',
      keywords: ['lettings agent', 'tenant find service', 'let my property']
    },
    'investments': {
      title: 'Property Investment',
      description: 'Property investment opportunities in West Yorkshire. Expert guidance on buy-to-let investments and portfolio building.',
      keywords: ['property investment', 'buy to let', 'investment properties West Yorkshire']
    }
  };

  const config = serviceConfig[service] || serviceConfig['property-management'];

  return (
    <SEO
      title={config.title}
      description={config.description}
      canonical={`/services/${service}`}
      keywords={config.keywords}
      schema={{
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: config.title,
        description: config.description,
        provider: {
          '@type': 'RealEstateAgent',
          name: 'Keystone Properties'
        },
        areaServed: {
          '@type': 'AdministrativeArea',
          name: 'West Yorkshire'
        }
      }}
    />
  );
};

export const LandlordsSEO = () => (
  <SEO
    title="For Landlords"
    description="Landlord services in West Yorkshire. Property management, tenant find, rent collection and maintenance for landlords in Leeds, Bradford, Batley and Dewsbury."
    canonical="/landlords"
    keywords={['landlord services', 'property management for landlords', 'let my property West Yorkshire']}
    schema={{
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Landlord Services',
      description: 'Comprehensive property management and letting services for landlords',
      provider: {
        '@type': 'RealEstateAgent',
        name: 'Keystone Properties'
      }
    }}
  />
);

export const TenantsSEO = () => (
  <SEO
    title="For Tenants"
    description="Tenant support and services from Keystone Properties. Report maintenance issues, find rental properties, and get help with your tenancy in West Yorkshire."
    canonical="/tenants"
    keywords={['tenant support', 'report maintenance', 'rental properties West Yorkshire']}
    schema={{
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Tenant Services',
      description: 'Support services for tenants in West Yorkshire',
      provider: {
        '@type': 'RealEstateAgent',
        name: 'Keystone Properties'
      }
    }}
  />
);

export const NewsSEO = () => (
  <SEO
    title="Property News & Insights"
    description="Latest property news, landlord tips, tenant advice and market insights for West Yorkshire. Expert guidance from Keystone Properties."
    canonical="/news"
    keywords={['property news', 'landlord tips', 'rental market insights', 'West Yorkshire property']}
  />
);

export const NewsArticleSEO = ({ article }) => (
  <SEO
    title={article?.title}
    description={article?.excerpt}
    canonical={`/news/${article?.slug}`}
    type="article"
    keywords={[article?.category?.replace('_', ' ')]}
    schema={{
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article?.title,
      description: article?.excerpt,
      author: {
        '@type': 'Organization',
        name: 'Keystone Properties'
      },
      publisher: {
        '@type': 'Organization',
        name: 'Keystone Properties',
        logo: {
          '@type': 'ImageObject',
          url: 'https://thekeystoneproperties.co.uk/logo.png'
        }
      },
      datePublished: article?.created_at,
      dateModified: article?.created_at
    }}
  />
);

export const AboutSEO = () => (
  <SEO
    title="About Us"
    description="Learn about Keystone Properties - your local property management and lettings experts in West Yorkshire. Based in Batley, serving Leeds, Bradford, Dewsbury and surrounding areas."
    canonical="/about"
    keywords={['about Keystone Properties', 'property agents Batley', 'local letting agents']}
    schema={{
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Keystone Properties',
      mainEntity: {
        '@type': 'RealEstateAgent',
        name: 'Keystone Properties',
        foundingDate: '2018',
        founder: {
          '@type': 'Person',
          name: 'Keystone Properties Team'
        }
      }
    }}
  />
);

export const ContactSEO = () => (
  <SEO
    title="Contact Us"
    description="Contact Keystone Properties for property management, lettings and investment enquiries. Call 01924 520163 or visit us at 17 Commercial Street, Batley."
    canonical="/contact"
    keywords={['contact Keystone Properties', 'property agents contact', 'lettings agent Batley']}
    schema={{
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact Keystone Properties',
      mainEntity: {
        '@type': 'RealEstateAgent',
        name: 'Keystone Properties',
        telephone: '01924 520163',
        email: 'info@thekeystoneproperties.co.uk'
      }
    }}
  />
);

export default SEO;
